import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc, 
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import AdminNav from './AdminNav';
import { 
  FaSave, 
  FaPlus, 
  FaTrash, 
  FaEdit, 
  FaTrophy, 
  FaPercentage, 
  FaCog, 
  FaHistory,
  FaSpinner,
  FaUpload,
  FaChevronDown,
  FaChevronUp,
  FaDollarSign,
  FaShippingFast,
  FaBox
} from 'react-icons/fa';

const TreasureHuntAdmin = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Game Configuration
  const [gameConfig, setGameConfig] = useState({
    enabled: true,
    pricePerPlay: 20,
    title: 'Treasure Hunt',
    description: 'Try your luck with our virtual claw machine!',
    videoUrl: '',
    videoDuration: 5000,
    idleImage: '',
    idleTitle: 'Ready to play?',
    idleSubtext: 'Press the button below to try your luck!'
  });

  // Prize Tiers
  const [prizeTiers, setPrizeTiers] = useState([]);
  const [editingTier, setEditingTier] = useState(null);
  const [newTier, setNewTier] = useState({
    name: '',
    odds: 0,
    value: 0,
    icon: 'bronze',
    image: '',
    items: []
  });

  // Play History
  const [playHistory, setPlayHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');

  // Shipping Requests
  const [shippingRequests, setShippingRequests] = useState([]);
  const [shippingFilter, setShippingFilter] = useState('all');

  // Video upload
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Idle image upload
  const [idleImageProgress, setIdleImageProgress] = useState(0);
  const [isUploadingIdleImage, setIsUploadingIdleImage] = useState(false);

  // Item management within tiers
  const [expandedTier, setExpandedTier] = useState(null);
  const [newItem, setNewItem] = useState({
    name: '',
    value: 0,
    image: '',
    quantity: 1
  });
  const [itemImageUploading, setItemImageUploading] = useState(false);

  // Profitability Simulator
  const [simResults, setSimResults] = useState(null);
  const [simPlays, setSimPlays] = useState(200);
  const [isSimulating, setIsSimulating] = useState(false);

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch game config
      const configDoc = await getDoc(doc(db, 'settings', 'treasureHunt'));
      if (configDoc.exists()) {
        setGameConfig(configDoc.data());
      }

      // Fetch prize tiers
      const tiersSnapshot = await getDocs(collection(db, 'treasureHuntPrizes'));
      const tiers = tiersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => b.odds - a.odds);
      setPrizeTiers(tiers);

      // Fetch play history
      const historySnapshot = await getDocs(collection(db, 'treasureHuntPlays'));
      const history = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => b.playedAt?.toDate() - a.playedAt?.toDate());
      setPlayHistory(history);

      // Fetch shipping requests
      const shippingSnapshot = await getDocs(collection(db, 'clawShippingRequests'));
      const requests = shippingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
      setShippingRequests(requests);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Save game configuration
  const saveGameConfig = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await setDoc(doc(db, 'settings', 'treasureHunt'), {
        ...gameConfig,
        updatedAt: serverTimestamp()
      });
      setSuccess('Configuration saved successfully!');
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setError('Please upload a video file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const storageRef = ref(storage, `treasure-hunt/videos/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload video');
          setIsUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setGameConfig(prev => ({ ...prev, videoUrl: downloadURL }));
          setIsUploading(false);
          setSuccess('Video uploaded successfully!');
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload video');
      setIsUploading(false);
    }
  };

  // Handle idle image upload
  const handleIdleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    setIsUploadingIdleImage(true);
    setIdleImageProgress(0);

    try {
      const storageRef = ref(storage, `treasure-hunt/idle-images/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setIdleImageProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload image');
          setIsUploadingIdleImage(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setGameConfig(prev => ({ ...prev, idleImage: downloadURL }));
          setIsUploadingIdleImage(false);
          setSuccess('Idle image uploaded successfully!');
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
      setIsUploadingIdleImage(false);
    }
  };

  // Add new prize tier
  const addPrizeTier = async () => {
    if (!newTier.name || newTier.odds <= 0) {
      setError('Please fill in tier name and odds');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await addDoc(collection(db, 'treasureHuntPrizes'), {
        ...newTier,
        createdAt: serverTimestamp()
      });
      
      setNewTier({
        name: '',
        odds: 0,
        value: 0,
        icon: 'bronze',
        image: '',
        items: []
      });
      
      await fetchData();
      setSuccess('Prize tier added successfully!');
    } catch (err) {
      console.error('Error adding tier:', err);
      setError('Failed to add prize tier');
    } finally {
      setSaving(false);
    }
  };

  // Update prize tier
  const updatePrizeTier = async (tierId, updates) => {
    setSaving(true);
    setError('');

    try {
      await updateDoc(doc(db, 'treasureHuntPrizes', tierId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      await fetchData();
      setEditingTier(null);
      setSuccess('Prize tier updated successfully!');
    } catch (err) {
      console.error('Error updating tier:', err);
      setError('Failed to update prize tier');
    } finally {
      setSaving(false);
    }
  };

  // Delete prize tier
  const deletePrizeTier = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this prize tier?')) return;

    setSaving(true);
    setError('');

    try {
      await deleteDoc(doc(db, 'treasureHuntPrizes', tierId));
      await fetchData();
      setSuccess('Prize tier deleted successfully!');
    } catch (err) {
      console.error('Error deleting tier:', err);
      setError('Failed to delete prize tier');
    } finally {
      setSaving(false);
    }
  };

  // Handle item image upload
  const handleItemImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    setItemImageUploading(true);

    try {
      const storageRef = ref(storage, `treasure-hunt/items/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        () => {},
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload image');
          setItemImageUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setNewItem(prev => ({ ...prev, image: downloadURL }));
          setItemImageUploading(false);
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
      setItemImageUploading(false);
    }
  };

  // Add item to a tier
  const addItemToTier = async (tierId) => {
    if (!newItem.name) {
      setError('Please enter an item name');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const tier = prizeTiers.find(t => t.id === tierId);
      const currentItems = tier?.items || [];
      
      // Create multiple items based on quantity
      const quantity = Math.max(1, newItem.quantity || 1);
      const newItems = [];
      
      for (let i = 0; i < quantity; i++) {
        newItems.push({
          name: newItem.name,
          value: newItem.value,
          image: newItem.image,
          id: `${Date.now()}_${i}`,
          createdAt: new Date().toISOString()
        });
      }

      await updateDoc(doc(db, 'treasureHuntPrizes', tierId), {
        items: [...currentItems, ...newItems],
        updatedAt: serverTimestamp()
      });

      setNewItem({ name: '', value: 0, image: '', quantity: 1 });
      await fetchData();
      setSuccess(`${quantity} item${quantity > 1 ? 's' : ''} added successfully!`);
    } catch (err) {
      console.error('Error adding item:', err);
      setError('Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  // Remove item from a tier
  const removeItemFromTier = async (tierId, itemId) => {
    if (!window.confirm('Are you sure you want to remove this item?')) return;

    setSaving(true);
    setError('');

    try {
      const tier = prizeTiers.find(t => t.id === tierId);
      const updatedItems = (tier?.items || []).filter(item => item.id !== itemId);

      await updateDoc(doc(db, 'treasureHuntPrizes', tierId), {
        items: updatedItems,
        updatedAt: serverTimestamp()
      });

      await fetchData();
      setSuccess('Item removed successfully!');
    } catch (err) {
      console.error('Error removing item:', err);
      setError('Failed to remove item');
    } finally {
      setSaving(false);
    }
  };

  // Update play status
  const updatePlayStatus = async (playId, newStatus) => {
    try {
      await updateDoc(doc(db, 'treasureHuntPlays', playId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      await fetchData();
      setSuccess('Status updated!');
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  // Update shipping request status
  const updateShippingStatus = async (requestId, newStatus, trackingNumber = '', carrier = 'usps') => {
    try {
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };
      
      if (trackingNumber) {
        updateData.trackingNumber = trackingNumber;
        updateData.carrier = carrier;
      }
      
      if (newStatus === 'shipped') {
        updateData.shippedAt = serverTimestamp();
      } else if (newStatus === 'delivered') {
        updateData.deliveredAt = serverTimestamp();
      }
      
      await updateDoc(doc(db, 'clawShippingRequests', requestId), updateData);
      await fetchData();
      setSuccess('Shipping status updated!');
    } catch (err) {
      console.error('Error updating shipping status:', err);
      setError('Failed to update shipping status');
    }
  };

  // Ship order with tracking and send notification email
  const shipOrderWithTracking = async (request, trackingNumber, carrier) => {
    if (!trackingNumber.trim()) {
      setError('Please enter a tracking number');
      return;
    }
    
    setSaving(true);
    try {
      // Update shipping request
      await updateDoc(doc(db, 'clawShippingRequests', request.id), {
        status: 'shipped',
        trackingNumber: trackingNumber.trim(),
        carrier: carrier,
        shippedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Send shipping notification email
      const response = await fetch('https://benevolent-sfogliatella-83141e.netlify.app/.netlify/functions/send-shipping-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: request.userEmail,
          customerName: request.shippingInfo?.name || 'Valued Customer',
          orderId: request.id,
          trackingNumber: trackingNumber.trim(),
          carrier: carrier,
          orderItems: request.items?.map(item => ({
            name: item.itemName,
            quantity: 1
          })) || [],
          shippingAddress: request.shippingInfo
        })
      });
      
      if (!response.ok) {
        console.error('Failed to send shipping notification email');
      }
      
      await fetchData();
      setSuccess('Order shipped and customer notified!');
    } catch (err) {
      console.error('Error shipping order:', err);
      setError('Failed to ship order');
    } finally {
      setSaving(false);
    }
  };

  // State for expanded shipping requests
  const [expandedShipping, setExpandedShipping] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});

  // Filter shipping requests
  const filteredShippingRequests = shippingFilter === 'all'
    ? shippingRequests
    : shippingRequests.filter(req => req.status === shippingFilter);

  // Calculate shipping stats
  const shippingStats = {
    total: shippingRequests.length,
    pending: shippingRequests.filter(r => r.status === 'pending_payment' || r.status === 'paid').length,
    shipped: shippingRequests.filter(r => r.status === 'shipped').length,
    delivered: shippingRequests.filter(r => r.status === 'delivered').length
  };

  // Calculate total odds
  const totalOdds = prizeTiers.reduce((sum, tier) => sum + (tier.odds || 0), 0);

  // Filter play history
  const filteredHistory = historyFilter === 'all' 
    ? playHistory 
    : playHistory.filter(play => play.status === historyFilter);

  // Calculate stats
  const stats = {
    totalPlays: playHistory.length,
    totalRevenue: playHistory.reduce((sum, play) => sum + (play.pricePaid || 0), 0),
    pendingShipments: playHistory.filter(p => p.status === 'pending_shipment').length,
    totalPrizeValue: playHistory.reduce((sum, play) => sum + (play.itemValue || 0), 0)
  };

  // Profitability Simulator Function
  const runSimulation = () => {
    if (prizeTiers.length === 0) {
      setError('No prize tiers configured. Add prize tiers first.');
      return;
    }

    setIsSimulating(true);
    setSimResults(null);

    // Simulate plays
    const results = {
      totalPlays: simPlays,
      pricePerPlay: gameConfig.pricePerPlay || 20,
      totalRevenue: simPlays * (gameConfig.pricePerPlay || 20),
      totalPrizeCost: 0,
      tierBreakdown: {},
      wins: []
    };

    // Initialize tier breakdown
    prizeTiers.forEach(tier => {
      results.tierBreakdown[tier.id] = {
        name: tier.name,
        odds: tier.odds,
        wins: 0,
        totalValue: 0,
        items: []
      };
    });

    // Create a copy of tiers with items for simulation (don't modify original)
    const simTiers = prizeTiers.map(tier => ({
      ...tier,
      items: [...(tier.items || [])]
    }));

    // Run simulation
    for (let i = 0; i < simPlays; i++) {
      // Filter tiers that have items available
      const availableTiers = simTiers.filter(tier => tier.items && tier.items.length > 0);
      
      if (availableTiers.length === 0) {
        // No items left, record as "no prize"
        results.wins.push({ tier: null, item: null, value: 0 });
        continue;
      }

      const totalOdds = availableTiers.reduce((sum, tier) => sum + tier.odds, 0);
      let random = Math.random() * totalOdds;
      
      let selectedTier = availableTiers[0];
      let selectedItem = null;
      let selectedItemIndex = -1;

      for (const tier of availableTiers) {
        random -= tier.odds;
        if (random <= 0) {
          selectedTier = tier;
          const items = tier.items || [];
          if (items.length > 0) {
            selectedItemIndex = Math.floor(Math.random() * items.length);
            selectedItem = items[selectedItemIndex];
            // Remove item from simulation pool
            tier.items.splice(selectedItemIndex, 1);
          }
          break;
        }
      }

      const prizeValue = selectedItem?.value || selectedTier?.value || 0;
      results.totalPrizeCost += prizeValue;
      
      if (selectedTier && results.tierBreakdown[selectedTier.id]) {
        results.tierBreakdown[selectedTier.id].wins++;
        results.tierBreakdown[selectedTier.id].totalValue += prizeValue;
        if (selectedItem) {
          results.tierBreakdown[selectedTier.id].items.push(selectedItem.name);
        }
      }

      results.wins.push({
        tier: selectedTier?.name,
        item: selectedItem?.name,
        value: prizeValue
      });
    }

    // Calculate final stats
    results.netProfit = results.totalRevenue - results.totalPrizeCost;
    results.profitMargin = ((results.netProfit / results.totalRevenue) * 100).toFixed(1);
    results.avgPrizeCost = (results.totalPrizeCost / simPlays).toFixed(2);
    results.isProfitable = results.netProfit > 0;

    setSimResults(results);
    setIsSimulating(false);
  };

  if (loading) {
    return (
      <Container>
        <AdminNav />
        <LoadingContainer>
          <FaSpinner className="spin" />
          <p>Loading Treasure Hunt Admin...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <AdminNav />
      
      <Header>
        <Title><FaTrophy /> Treasure Hunt Management</Title>
        <Subtitle>Configure your online claw machine game</Subtitle>
      </Header>

      {/* Stats Cards */}
      <StatsGrid>
        <StatCard>
          <StatIcon><FaHistory /></StatIcon>
          <StatInfo>
            <StatValue>{stats.totalPlays}</StatValue>
            <StatLabel>Total Plays</StatLabel>
          </StatInfo>
        </StatCard>
        <StatCard>
          <StatIcon><FaDollarSign /></StatIcon>
          <StatInfo>
            <StatValue>${stats.totalRevenue.toFixed(2)}</StatValue>
            <StatLabel>Total Revenue</StatLabel>
          </StatInfo>
        </StatCard>
        <StatCard>
          <StatIcon><FaTrophy /></StatIcon>
          <StatInfo>
            <StatValue>${stats.totalPrizeValue.toFixed(2)}</StatValue>
            <StatLabel>Prizes Given</StatLabel>
          </StatInfo>
        </StatCard>
        <StatCard $highlight>
          <StatIcon><FaPercentage /></StatIcon>
          <StatInfo>
            <StatValue>${(stats.totalRevenue - stats.totalPrizeValue).toFixed(2)}</StatValue>
            <StatLabel>Net Profit</StatLabel>
          </StatInfo>
        </StatCard>
      </StatsGrid>

      {/* Tabs */}
      <TabsContainer>
        <Tab $active={activeTab === 'config'} onClick={() => setActiveTab('config')}>
          <FaCog /> Configuration
        </Tab>
        <Tab $active={activeTab === 'prizes'} onClick={() => setActiveTab('prizes')}>
          <FaTrophy /> Prize Tiers ({prizeTiers.length})
        </Tab>
        <Tab $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          <FaHistory /> Play History ({playHistory.length})
        </Tab>
        <Tab $active={activeTab === 'shipping'} onClick={() => setActiveTab('shipping')}>
          <FaShippingFast /> Shipping ({shippingRequests.length})
        </Tab>
        <Tab $active={activeTab === 'simulator'} onClick={() => setActiveTab('simulator')}>
          📊 Profitability Simulator
        </Tab>
      </TabsContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <ContentCard>
          <SectionTitle>Game Settings</SectionTitle>
          
          <FormGrid>
            <FormGroup>
              <Label>Game Enabled</Label>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={gameConfig.enabled}
                  onChange={(e) => setGameConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                />
                <span className="slider"></span>
              </ToggleSwitch>
              <ToggleLabel>{gameConfig.enabled ? 'Active' : 'Disabled'}</ToggleLabel>
            </FormGroup>

            <FormGroup>
              <Label>Price Per Play ($)</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={gameConfig.pricePerPlay}
                onChange={(e) => setGameConfig(prev => ({ ...prev, pricePerPlay: parseFloat(e.target.value) || 0 }))}
              />
            </FormGroup>

            <FormGroup $fullWidth>
              <Label>Game Title</Label>
              <Input
                type="text"
                value={gameConfig.title}
                onChange={(e) => setGameConfig(prev => ({ ...prev, title: e.target.value }))}
              />
            </FormGroup>

            <FormGroup $fullWidth>
              <Label>Description</Label>
              <TextArea
                value={gameConfig.description}
                onChange={(e) => setGameConfig(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </FormGroup>

            <FormGroup $fullWidth>
              <Label>Claw Animation Video</Label>
              <VideoUploadContainer>
                {gameConfig.videoUrl && (
                  <VideoPreview>
                    <video src={gameConfig.videoUrl} controls width="300">
                      <track kind="captions" />
                    </video>
                  </VideoPreview>
                )}
                <UploadButton>
                  <FaUpload /> {gameConfig.videoUrl ? 'Replace Video' : 'Upload Video'}
                  <input type="file" accept="video/*" onChange={handleVideoUpload} />
                </UploadButton>
                {isUploading && (
                  <ProgressBar>
                    <Progress value={uploadProgress} />
                    <span>{uploadProgress.toFixed(0)}%</span>
                  </ProgressBar>
                )}
              </VideoUploadContainer>
            </FormGroup>

            <FormGroup>
              <Label>Video Duration (ms)</Label>
              <Input
                type="number"
                min="1000"
                step="500"
                value={gameConfig.videoDuration}
                onChange={(e) => setGameConfig(prev => ({ ...prev, videoDuration: parseInt(e.target.value) || 5000 }))}
              />
              <HelpText>How long to show the animation before revealing prize</HelpText>
            </FormGroup>

            <FormGroup $fullWidth>
              <Label>Idle State Image (Optional)</Label>
              <VideoUploadContainer>
                {gameConfig.idleImage && (
                  <VideoPreview>
                    <img src={gameConfig.idleImage} alt="Idle state" style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }} />
                  </VideoPreview>
                )}
                <UploadButton>
                  <FaUpload /> {gameConfig.idleImage ? 'Replace Image' : 'Upload Image'}
                  <input type="file" accept="image/*" onChange={handleIdleImageUpload} />
                </UploadButton>
                {isUploadingIdleImage && (
                  <ProgressBar>
                    <Progress value={idleImageProgress} />
                    <span>{idleImageProgress.toFixed(0)}%</span>
                  </ProgressBar>
                )}
              </VideoUploadContainer>
              <HelpText>Custom image to display when game is idle (shows coin emoji if not set)</HelpText>
            </FormGroup>

            <FormGroup $fullWidth>
              <Label>Idle Title Text</Label>
              <Input
                type="text"
                value={gameConfig.idleTitle || 'Ready to play?'}
                onChange={(e) => setGameConfig(prev => ({ ...prev, idleTitle: e.target.value }))}
                placeholder="Ready to play?"
              />
              <HelpText>Main text shown when game is idle</HelpText>
            </FormGroup>

            <FormGroup $fullWidth>
              <Label>Idle Subtext</Label>
              <Input
                type="text"
                value={gameConfig.idleSubtext || 'Press the button below to try your luck!'}
                onChange={(e) => setGameConfig(prev => ({ ...prev, idleSubtext: e.target.value }))}
                placeholder="Press the button below to try your luck!"
              />
              <HelpText>Secondary text shown when game is idle</HelpText>
            </FormGroup>
          </FormGrid>

          <SaveButton onClick={saveGameConfig} disabled={saving}>
            {saving ? <FaSpinner className="spin" /> : <FaSave />}
            Save Configuration
          </SaveButton>
        </ContentCard>
      )}

      {/* Prize Tiers Tab */}
      {activeTab === 'prizes' && (
        <ContentCard>
          <SectionHeader>
            <SectionTitle>Prize Tiers</SectionTitle>
            <OddsTotal $valid={Math.abs(totalOdds - 100) < 0.01}>
              Total Odds: {totalOdds.toFixed(1)}% {Math.abs(totalOdds - 100) < 0.01 ? '✓' : '(should equal 100%)'}
            </OddsTotal>
          </SectionHeader>

          {/* Add New Tier Form */}
          <AddTierSection>
            <h4><FaPlus /> Add New Prize Tier</h4>
            <TierForm>
              <FormGroup>
                <Label>Tier Name</Label>
                <Input
                  type="text"
                  placeholder="e.g., Copper Round"
                  value={newTier.name}
                  onChange={(e) => setNewTier(prev => ({ ...prev, name: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Odds (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="e.g., 80"
                  value={newTier.odds || ''}
                  onChange={(e) => setNewTier(prev => ({ ...prev, odds: parseFloat(e.target.value) || 0 }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Value ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g., 5"
                  value={newTier.value || ''}
                  onChange={(e) => setNewTier(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Icon</Label>
                <Select
                  value={newTier.icon}
                  onChange={(e) => setNewTier(prev => ({ ...prev, icon: e.target.value }))}
                >
                  <option value="bronze">🥉 Bronze</option>
                  <option value="silver">🥈 Silver</option>
                  <option value="gold">🥇 Gold</option>
                  <option value="gem">💎 Gem</option>
                </Select>
              </FormGroup>
              <AddButton onClick={addPrizeTier} disabled={saving}>
                <FaPlus /> Add Tier
              </AddButton>
            </TierForm>
          </AddTierSection>

          {/* Existing Tiers */}
          <TiersList>
            {prizeTiers.map(tier => (
              <TierCard key={tier.id}>
                <TierHeader>
                  <TierIcon>
                    {tier.icon === 'gold' ? '🥇' : 
                     tier.icon === 'silver' ? '🥈' : 
                     tier.icon === 'gem' ? '💎' : '🥉'}
                  </TierIcon>
                  <TierName>{tier.name}</TierName>
                  <TierActions>
                    <ActionButton onClick={() => setEditingTier(editingTier === tier.id ? null : tier.id)}>
                      {editingTier === tier.id ? <FaChevronUp /> : <FaEdit />}
                    </ActionButton>
                    <ActionButton $danger onClick={() => deletePrizeTier(tier.id)}>
                      <FaTrash />
                    </ActionButton>
                  </TierActions>
                </TierHeader>
                
                <TierStats>
                  <TierStat>
                    <span>Odds:</span>
                    <strong>{tier.odds}%</strong>
                  </TierStat>
                  <TierStat>
                    <span>Value:</span>
                    <strong>${tier.value}</strong>
                  </TierStat>
                  <TierStat>
                    <span>Items:</span>
                    <strong>{tier.items?.length || 0}</strong>
                  </TierStat>
                </TierStats>

                {editingTier === tier.id && (
                  <TierEditForm>
                    <FormGroup>
                      <Label>Name</Label>
                      <Input
                        type="text"
                        defaultValue={tier.name}
                        id={`tier-name-${tier.id}`}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Odds (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        defaultValue={tier.odds}
                        id={`tier-odds-${tier.id}`}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Value ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={tier.value}
                        id={`tier-value-${tier.id}`}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Icon</Label>
                      <Select defaultValue={tier.icon} id={`tier-icon-${tier.id}`}>
                        <option value="bronze">🥉 Bronze</option>
                        <option value="silver">🥈 Silver</option>
                        <option value="gold">🥇 Gold</option>
                        <option value="gem">💎 Gem</option>
                      </Select>
                    </FormGroup>
                    <SaveButton 
                      onClick={() => {
                        const updates = {
                          name: document.getElementById(`tier-name-${tier.id}`).value,
                          odds: parseFloat(document.getElementById(`tier-odds-${tier.id}`).value) || 0,
                          value: parseFloat(document.getElementById(`tier-value-${tier.id}`).value) || 0,
                          icon: document.getElementById(`tier-icon-${tier.id}`).value
                        };
                        updatePrizeTier(tier.id, updates);
                      }}
                      disabled={saving}
                    >
                      <FaSave /> Save Changes
                    </SaveButton>
                  </TierEditForm>
                )}

                {/* Items Section - Always visible, expandable */}
                <ItemsSection>
                  <ItemsHeader onClick={() => setExpandedTier(expandedTier === tier.id ? null : tier.id)}>
                    <span>📦 Items in this tier ({tier.items?.length || 0})</span>
                    {expandedTier === tier.id ? <FaChevronUp /> : <FaChevronDown />}
                  </ItemsHeader>
                  
                  {expandedTier === tier.id && (
                    <ItemsContent>
                      {/* Current Items List */}
                      {tier.items && tier.items.length > 0 ? (
                        <ItemsList>
                          {tier.items.map(item => (
                            <ItemCard key={item.id}>
                              {item.image ? (
                                <ItemImage src={item.image} alt={item.name} />
                              ) : (
                                <ItemImagePlaceholder>📦</ItemImagePlaceholder>
                              )}
                              <ItemInfo>
                                <ItemName>{item.name}</ItemName>
                                <ItemValue>${item.value?.toFixed(2) || '0.00'}</ItemValue>
                              </ItemInfo>
                              <RemoveItemButton onClick={() => removeItemFromTier(tier.id, item.id)}>
                                <FaTrash />
                              </RemoveItemButton>
                            </ItemCard>
                          ))}
                        </ItemsList>
                      ) : (
                        <NoItemsMessage>No items in this tier yet. Add items below!</NoItemsMessage>
                      )}

                      {/* Add New Item Form */}
                      <AddItemForm>
                        <AddItemTitle><FaPlus /> Add New Item</AddItemTitle>
                        <AddItemGrid>
                          <FormGroup>
                            <Label>Item Name *</Label>
                            <Input
                              type="text"
                              placeholder="e.g., 1oz Copper Round"
                              value={newItem.name}
                              onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>Value ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="e.g., 5.00"
                              value={newItem.value || ''}
                              onChange={(e) => setNewItem(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              placeholder="1"
                              value={newItem.quantity || 1}
                              onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                            />
                            <HelpText>Add multiple of the same item at once</HelpText>
                          </FormGroup>
                          <FormGroup>
                            <Label>Image</Label>
                            <ImageUploadArea>
                              {newItem.image ? (
                                <PreviewImage src={newItem.image} alt="Preview" />
                              ) : (
                                <UploadPlaceholder>
                                  {itemImageUploading ? <FaSpinner className="spin" /> : <FaUpload />}
                                  <span>{itemImageUploading ? 'Uploading...' : 'Upload Image'}</span>
                                </UploadPlaceholder>
                              )}
                              <input type="file" accept="image/*" onChange={handleItemImageUpload} />
                            </ImageUploadArea>
                          </FormGroup>
                        </AddItemGrid>
                        <AddItemButton onClick={() => addItemToTier(tier.id)} disabled={saving || !newItem.name}>
                          <FaPlus /> Add Item to {tier.name}
                        </AddItemButton>
                      </AddItemForm>
                    </ItemsContent>
                  )}
                </ItemsSection>
              </TierCard>
            ))}
          </TiersList>

          {prizeTiers.length === 0 && (
            <EmptyState>
              <p>No prize tiers configured yet.</p>
              <p>Add your first tier above to get started!</p>
            </EmptyState>
          )}
        </ContentCard>
      )}

      {/* Play History Tab */}
      {activeTab === 'history' && (
        <ContentCard>
          <SectionHeader>
            <SectionTitle>Play History</SectionTitle>
            <FilterGroup>
              <Label>Filter:</Label>
              <Select value={historyFilter} onChange={(e) => setHistoryFilter(e.target.value)}>
                <option value="all">All ({playHistory.length})</option>
                <option value="pending_shipment">Pending ({stats.pendingShipments})</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
              </Select>
            </FilterGroup>
          </SectionHeader>

          <HistoryTable>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Prize Won</th>
                <th>Value</th>
                <th>Paid</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map(play => (
                <tr key={play.id}>
                  <td>{play.playedAt?.toDate()?.toLocaleDateString() || 'N/A'}</td>
                  <td>{play.userEmail || 'Unknown'}</td>
                  <td>{play.itemName || play.tierName}</td>
                  <td>${play.itemValue?.toFixed(2) || '0.00'}</td>
                  <td>${play.pricePaid?.toFixed(2) || '0.00'}</td>
                  <td>
                    <StatusBadge $status={play.status}>
                      {play.status === 'pending_shipment' ? 'Pending' :
                       play.status === 'shipped' ? 'Shipped' :
                       play.status === 'delivered' ? 'Delivered' : play.status}
                    </StatusBadge>
                  </td>
                  <td>
                    <StatusSelect
                      value={play.status}
                      onChange={(e) => updatePlayStatus(play.id, e.target.value)}
                    >
                      <option value="pending_shipment">Pending</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                    </StatusSelect>
                  </td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>

          {filteredHistory.length === 0 && (
            <EmptyState>
              <p>No plays found.</p>
            </EmptyState>
          )}
        </ContentCard>
      )}

      {/* Shipping Requests Tab */}
      {activeTab === 'shipping' && (
        <ContentCard>
          <SectionHeader>
            <SectionTitle><FaShippingFast /> Shipping Requests</SectionTitle>
            <FilterGroup>
              <Label>Filter:</Label>
              <Select value={shippingFilter} onChange={(e) => setShippingFilter(e.target.value)}>
                <option value="all">All ({shippingStats.total})</option>
                <option value="paid">Ready to Ship ({shippingStats.pending})</option>
                <option value="shipped">Shipped ({shippingStats.shipped})</option>
                <option value="delivered">Delivered ({shippingStats.delivered})</option>
              </Select>
            </FilterGroup>
          </SectionHeader>

          {filteredShippingRequests.length > 0 ? (
            <ShippingRequestsList>
              {filteredShippingRequests.map(request => (
                <ShippingRequestCard key={request.id} $expanded={expandedShipping === request.id}>
                  <ShippingRequestHeader onClick={() => setExpandedShipping(expandedShipping === request.id ? null : request.id)}>
                    <ShippingRequestInfo>
                      <ShippingRequestDate>
                        {request.createdAt?.toDate()?.toLocaleDateString() || 'N/A'}
                      </ShippingRequestDate>
                      <ShippingRequestCustomer>
                        <strong>{request.shippingInfo?.name || 'N/A'}</strong>
                        <span>{request.userEmail}</span>
                      </ShippingRequestCustomer>
                      <ShippingRequestItemCount>
                        {request.items?.length || 0} item{request.items?.length !== 1 ? 's' : ''}
                      </ShippingRequestItemCount>
                      <StatusBadge $status={request.status}>
                        {request.status === 'pending_payment' ? 'Awaiting Payment' :
                         request.status === 'paid' ? 'Ready to Ship' :
                         request.status === 'shipped' ? 'Shipped' :
                         request.status === 'delivered' ? 'Delivered' : request.status}
                      </StatusBadge>
                    </ShippingRequestInfo>
                    <ExpandIcon>
                      {expandedShipping === request.id ? <FaChevronUp /> : <FaChevronDown />}
                    </ExpandIcon>
                  </ShippingRequestHeader>
                  
                  {expandedShipping === request.id && (
                    <ShippingRequestDetails>
                      <ShippingDetailsGrid>
                        <ShippingDetailSection>
                          <h4><FaBox /> Items to Ship ({request.items?.length || 0})</h4>
                          <ShippingItemsList>
                            {request.items?.map((item, idx) => (
                              <ShippingItem key={idx}>
                                {item.itemImage && (
                                  <ShippingItemImage src={item.itemImage} alt={item.itemName} />
                                )}
                                <ShippingItemInfo>
                                  <div className="name">{item.itemName}</div>
                                  <div className="value">Value: ${item.itemValue?.toFixed(2) || '0.00'}</div>
                                </ShippingItemInfo>
                              </ShippingItem>
                            ))}
                          </ShippingItemsList>
                        </ShippingDetailSection>
                        
                        <ShippingDetailSection>
                          <h4><FaShippingFast /> Shipping Address</h4>
                          <ShippingAddressBox>
                            <p><strong>{request.shippingInfo?.name}</strong></p>
                            <p>{request.shippingInfo?.address}</p>
                            <p>{request.shippingInfo?.city}, {request.shippingInfo?.state} {request.shippingInfo?.zipCode}</p>
                            {request.shippingInfo?.phone && <p>Phone: {request.shippingInfo.phone}</p>}
                          </ShippingAddressBox>
                          
                          <ShippingFeeBox>
                            <span>Shipping Fee Paid:</span>
                            <strong>${request.shippingFee?.toFixed(2) || '0.00'}</strong>
                          </ShippingFeeBox>
                        </ShippingDetailSection>
                      </ShippingDetailsGrid>
                      
                      {/* Tracking Section */}
                      {request.status === 'paid' && (
                        <TrackingSection>
                          <h4>Add Tracking & Ship</h4>
                          <TrackingInputGroup>
                            <TrackingInput
                              type="text"
                              placeholder="Enter tracking number"
                              value={trackingInputs[request.id]?.tracking || ''}
                              onChange={(e) => setTrackingInputs(prev => ({
                                ...prev,
                                [request.id]: { ...prev[request.id], tracking: e.target.value }
                              }))}
                            />
                            <CarrierSelect
                              value={trackingInputs[request.id]?.carrier || 'usps'}
                              onChange={(e) => setTrackingInputs(prev => ({
                                ...prev,
                                [request.id]: { ...prev[request.id], carrier: e.target.value }
                              }))}
                            >
                              <option value="usps">USPS</option>
                              <option value="ups">UPS</option>
                              <option value="fedex">FedEx</option>
                              <option value="dhl">DHL</option>
                            </CarrierSelect>
                            <ShipButton
                              onClick={() => shipOrderWithTracking(
                                request,
                                trackingInputs[request.id]?.tracking || '',
                                trackingInputs[request.id]?.carrier || 'usps'
                              )}
                              disabled={saving}
                            >
                              {saving ? <><FaSpinner className="spin" /> Shipping...</> : 'Mark as Shipped'}
                            </ShipButton>
                          </TrackingInputGroup>
                        </TrackingSection>
                      )}
                      
                      {/* Show tracking info if shipped */}
                      {(request.status === 'shipped' || request.status === 'delivered') && request.trackingNumber && (
                        <TrackingInfoBox>
                          <h4>Tracking Information</h4>
                          <p><strong>Carrier:</strong> {request.carrier?.toUpperCase() || 'USPS'}</p>
                          <p><strong>Tracking #:</strong> {request.trackingNumber}</p>
                          {request.shippedAt && (
                            <p><strong>Shipped:</strong> {request.shippedAt?.toDate()?.toLocaleDateString()}</p>
                          )}
                        </TrackingInfoBox>
                      )}
                      
                      {/* Status update for shipped orders */}
                      {request.status === 'shipped' && (
                        <StatusUpdateSection>
                          <DeliveredButton onClick={() => updateShippingStatus(request.id, 'delivered')}>
                            Mark as Delivered
                          </DeliveredButton>
                        </StatusUpdateSection>
                      )}
                    </ShippingRequestDetails>
                  )}
                </ShippingRequestCard>
              ))}
            </ShippingRequestsList>
          ) : (
            <EmptyState>
              <FaBox size={40} style={{ marginBottom: '1rem', color: '#ccc' }} />
              <p>No shipping requests found.</p>
            </EmptyState>
          )}
        </ContentCard>
      )}

      {/* Profitability Simulator Tab */}
      {activeTab === 'simulator' && (
        <ContentCard>
          <SectionHeader>
            <SectionTitle>📊 Profitability Simulator</SectionTitle>
          </SectionHeader>
          
          <SimulatorInfo>
            <p>Test your prize configuration without affecting the actual game. This simulation uses the same weighted random algorithm as the real game.</p>
          </SimulatorInfo>

          <SimulatorControls>
            <SimInputGroup>
              <label>Number of Plays to Simulate:</label>
              <SimInput
                type="number"
                value={simPlays}
                onChange={(e) => setSimPlays(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                max="10000"
              />
            </SimInputGroup>
            <SimInputGroup>
              <label>Price per Play:</label>
              <SimInput
                type="text"
                value={`$${gameConfig.pricePerPlay || 20}`}
                disabled
              />
            </SimInputGroup>
            <SimInputGroup>
              <label>Total Items in Pool:</label>
              <SimInput
                type="text"
                value={prizeTiers.reduce((sum, tier) => sum + (tier.items?.length || 0), 0)}
                disabled
              />
            </SimInputGroup>
            <RunSimButton onClick={runSimulation} disabled={isSimulating}>
              {isSimulating ? <><FaSpinner className="spin" /> Simulating...</> : '🎮 Run Simulation'}
            </RunSimButton>
          </SimulatorControls>

          {simResults && (
            <SimResultsContainer>
              <SimResultsHeader $profitable={simResults.isProfitable}>
                <h2>{simResults.isProfitable ? '✅ PROFITABLE' : '❌ NOT PROFITABLE'}</h2>
                <SimProfitMargin $profitable={simResults.isProfitable}>
                  {simResults.profitMargin}% Margin
                </SimProfitMargin>
              </SimResultsHeader>

              <SimStatsGrid>
                <SimStatCard>
                  <SimStatLabel>Total Revenue</SimStatLabel>
                  <SimStatValue $positive>{`$${simResults.totalRevenue.toLocaleString()}`}</SimStatValue>
                  <SimStatSubtext>{simResults.totalPlays} plays × ${simResults.pricePerPlay}</SimStatSubtext>
                </SimStatCard>
                
                <SimStatCard>
                  <SimStatLabel>Total Prize Cost</SimStatLabel>
                  <SimStatValue $negative>{`$${simResults.totalPrizeCost.toLocaleString()}`}</SimStatValue>
                  <SimStatSubtext>Avg ${simResults.avgPrizeCost}/play</SimStatSubtext>
                </SimStatCard>
                
                <SimStatCard $highlight={simResults.isProfitable}>
                  <SimStatLabel>Net Profit/Loss</SimStatLabel>
                  <SimStatValue $positive={simResults.isProfitable} $negative={!simResults.isProfitable}>
                    {simResults.netProfit >= 0 ? '+' : ''}{`$${simResults.netProfit.toLocaleString()}`}
                  </SimStatValue>
                  <SimStatSubtext>{simResults.profitMargin}% profit margin</SimStatSubtext>
                </SimStatCard>
              </SimStatsGrid>

              <SimTierBreakdown>
                <h3>Tier Breakdown</h3>
                <SimTierTable>
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th>Odds</th>
                      <th>Wins</th>
                      <th>Win Rate</th>
                      <th>Total Value</th>
                      <th>Avg Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(simResults.tierBreakdown).map((tier, idx) => (
                      <tr key={idx}>
                        <td>{tier.name}</td>
                        <td>{tier.odds}%</td>
                        <td>{tier.wins}</td>
                        <td>{((tier.wins / simResults.totalPlays) * 100).toFixed(1)}%</td>
                        <td>${tier.totalValue.toLocaleString()}</td>
                        <td>${tier.wins > 0 ? (tier.totalValue / tier.wins).toFixed(2) : '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </SimTierTable>
              </SimTierBreakdown>

              <SimRecommendations>
                <h3>💡 Recommendations</h3>
                {simResults.isProfitable ? (
                  <SimRecList>
                    <li>✅ Your current configuration is profitable at {simResults.profitMargin}% margin</li>
                    {parseFloat(simResults.profitMargin) < 20 && (
                      <li>⚠️ Consider increasing price or reducing high-value prize odds for better margins</li>
                    )}
                    {parseFloat(simResults.profitMargin) > 50 && (
                      <li>💡 High profit margin - consider adding more valuable prizes to attract players</li>
                    )}
                  </SimRecList>
                ) : (
                  <SimRecList>
                    <li>❌ Current configuration results in a loss of ${Math.abs(simResults.netProfit).toLocaleString()}</li>
                    <li>💡 Consider increasing the price per play (currently ${simResults.pricePerPlay})</li>
                    <li>💡 Reduce odds of high-value tiers or add lower-value items</li>
                    <li>💡 Add more low-value items to dilute the prize pool</li>
                  </SimRecList>
                )}
              </SimRecommendations>
            </SimResultsContainer>
          )}
        </ContentCard>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #666;
  
  .spin {
    animation: spin 1s linear infinite;
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: ${props => props.$highlight ? 'linear-gradient(135deg, #D4AF37, #F4D03F)' : 'white'};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const StatIcon = styled.div`
  font-size: 1.5rem;
  color: #D4AF37;
`;

const StatInfo = styled.div``;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.$active ? '#D4AF37' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#666'};
  border: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$active ? '#D4AF37' : '#f5f5f5'};
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  color: #333;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 26px;
    
    &::before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + .slider {
    background-color: #4CAF50;
  }
  
  input:checked + .slider::before {
    transform: translateX(24px);
  }
`;

const ToggleLabel = styled.span`
  margin-left: 0.75rem;
  color: #666;
`;

const VideoUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const VideoPreview = styled.div`
  video {
    border-radius: 8px;
    max-width: 100%;
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #f5f5f5;
  border: 1px dashed #ccc;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #eee;
  }
  
  input {
    display: none;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  span {
    color: #666;
    font-size: 0.9rem;
  }
`;

const Progress = styled.div`
  flex: 1;
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    width: ${props => props.value}%;
    height: 100%;
    background: #D4AF37;
    transition: width 0.3s;
  }
`;

const HelpText = styled.p`
  font-size: 0.85rem;
  color: #888;
  margin-top: 0.25rem;
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #D4AF37;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1.5rem;
  transition: all 0.2s;
  
  &:hover {
    background: #c9a227;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
`;

const OddsTotal = styled.div`
  padding: 0.5rem 1rem;
  background: ${props => props.$valid ? '#e8f5e9' : '#fff3e0'};
  color: ${props => props.$valid ? '#2e7d32' : '#e65100'};
  border-radius: 6px;
  font-weight: 500;
`;

const AddTierSection = styled.div`
  background: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  
  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    color: #333;
  }
`;

const TierForm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  align-items: end;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #43a047;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TiersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TierCard = styled.div`
  border: 1px solid #eee;
  border-radius: 10px;
  overflow: hidden;
`;

const TierHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f9f9f9;
`;

const TierIcon = styled.div`
  font-size: 1.5rem;
  margin-right: 1rem;
`;

const TierName = styled.div`
  flex: 1;
  font-weight: 600;
  font-size: 1.1rem;
`;

const TierActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${props => props.$danger ? '#ffebee' : '#f5f5f5'};
  color: ${props => props.$danger ? '#c62828' : '#666'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.$danger ? '#ffcdd2' : '#eee'};
  }
`;

const TierStats = styled.div`
  display: flex;
  padding: 1rem;
  gap: 2rem;
  border-top: 1px solid #eee;
`;

const TierStat = styled.div`
  span {
    color: #888;
    font-size: 0.9rem;
  }
  
  strong {
    margin-left: 0.5rem;
    color: #333;
  }
`;

const TierEditForm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  padding: 1rem;
  background: #fafafa;
  border-top: 1px solid #eee;
  align-items: end;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  ${Label} {
    margin: 0;
  }
  
  ${Select} {
    width: auto;
    min-width: 150px;
  }
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f9f9f9;
    font-weight: 600;
    color: #555;
  }
  
  tbody tr:hover {
    background: #fafafa;
  }
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${props => 
    props.$status === 'delivered' ? '#e8f5e9' :
    props.$status === 'shipped' ? '#e3f2fd' :
    '#fff3e0'
  };
  color: ${props => 
    props.$status === 'delivered' ? '#2e7d32' :
    props.$status === 'shipped' ? '#1565c0' :
    '#e65100'
  };
`;

const StatusSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #888;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  padding: 1rem;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

// Items Management Styled Components
const ItemsSection = styled.div`
  border-top: 1px solid #eee;
`;

const ItemsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f5f5f5;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #eeeeee;
  }
  
  span {
    font-weight: 500;
    color: #555;
  }
  
  svg {
    color: #888;
  }
`;

const ItemsContent = styled.div`
  padding: 1.5rem;
  background: #fafafa;
`;

const ItemsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ItemCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: box-shadow 0.2s;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
`;

const ItemImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
`;

const ItemImagePlaceholder = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 6px;
  font-size: 1.5rem;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemValue = styled.div`
  color: #4CAF50;
  font-size: 0.85rem;
  font-weight: 600;
`;

const RemoveItemButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: #ffebee;
  color: #c62828;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  
  &:hover {
    background: #ffcdd2;
  }
`;

const NoItemsMessage = styled.p`
  text-align: center;
  color: #888;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const AddItemForm = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px dashed #D4AF37;
`;

const AddItemTitle = styled.h5`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  color: #D4AF37;
  font-size: 1rem;
`;

const AddItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ImageUploadArea = styled.label`
  display: block;
  width: 100%;
  height: 80px;
  border: 2px dashed #ddd;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  
  input {
    display: none;
  }
  
  &:hover {
    border-color: #D4AF37;
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #888;
  
  svg {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }
  
  span {
    font-size: 0.8rem;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const AddItemButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  background: #D4AF37;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #c9a227;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ShippingTable = styled(HistoryTable)``;

const ItemsPreview = styled.div`
  max-width: 200px;
`;

const AddressPreview = styled.div`
  font-size: 0.85rem;
  max-width: 200px;
  
  div:first-child {
    font-weight: 500;
  }
  
  div:last-child {
    color: #666;
  }
`;

const ShippingRequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ShippingRequestCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${props => props.$expanded ? '#D4AF37' : '#e0e0e0'};
  overflow: hidden;
  transition: all 0.2s;
`;

const ShippingRequestHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  cursor: pointer;
  
  &:hover {
    background: #f9f9f9;
  }
`;

const ShippingRequestInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const ShippingRequestDate = styled.div`
  color: #666;
  font-size: 0.9rem;
  min-width: 80px;
`;

const ShippingRequestCustomer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  
  strong {
    color: #333;
  }
  
  span {
    color: #888;
    font-size: 0.85rem;
  }
`;

const ShippingRequestItemCount = styled.div`
  background: #f0f0f0;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #666;
`;

const ExpandIcon = styled.div`
  color: #888;
  font-size: 1rem;
`;

const ShippingRequestDetails = styled.div`
  padding: 1.5rem;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
`;

const ShippingDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ShippingDetailSection = styled.div`
  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #333;
    margin: 0 0 1rem 0;
    font-size: 1rem;
  }
`;

const ShippingItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ShippingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const ShippingItemImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 6px;
  background: #f5f5f5;
`;

const ShippingItemInfo = styled.div`
  .name {
    font-weight: 500;
    color: #333;
  }
  
  .value {
    font-size: 0.85rem;
    color: #4CAF50;
  }
`;

const ShippingAddressBox = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  margin-bottom: 1rem;
  
  p {
    margin: 0.25rem 0;
    color: #333;
  }
`;

const ShippingFeeBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #e8f5e9;
  border-radius: 8px;
  
  span {
    color: #666;
  }
  
  strong {
    color: #2e7d32;
    font-size: 1.1rem;
  }
`;

const TrackingSection = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: #fff8e1;
  border-radius: 8px;
  border: 1px solid #ffd54f;
  
  h4 {
    margin: 0 0 1rem 0;
    color: #f57c00;
  }
`;

const TrackingInputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TrackingInput = styled.input`
  flex: 2;
  min-width: 200px;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
  }
`;

const CarrierSelect = styled.select`
  flex: 1;
  min-width: 100px;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
`;

const ShipButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover:not(:disabled) {
    background: #43a047;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
`;

const TrackingInfoBox = styled.div`
  margin-top: 1.5rem;
  padding: 1rem 1.5rem;
  background: #e3f2fd;
  border-radius: 8px;
  border: 1px solid #90caf9;
  
  h4 {
    margin: 0 0 0.75rem 0;
    color: #1565c0;
  }
  
  p {
    margin: 0.25rem 0;
    color: #333;
  }
`;

const StatusUpdateSection = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: flex-end;
`;

const DeliveredButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #2196F3;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #1976D2;
  }
`;

// Simulator Styled Components
const SimulatorInfo = styled.div`
  background: #e3f2fd;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #2196F3;
  
  p {
    margin: 0;
    color: #1565c0;
  }
`;

const SimulatorControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f5f5f5;
  border-radius: 8px;
`;

const SimInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: #666;
    font-size: 0.9rem;
  }
`;

const SimInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  width: 150px;
  
  &:disabled {
    background: #e0e0e0;
    color: #666;
  }
`;

const RunSimButton = styled.button`
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #9c27b0, #673ab7);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(156, 39, 176, 0.4);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
`;

const SimResultsContainer = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
`;

const SimResultsHeader = styled.div`
  background: ${props => props.$profitable ? 'linear-gradient(135deg, #4CAF50, #8BC34A)' : 'linear-gradient(135deg, #f44336, #e91e63)'};
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    color: white;
    font-size: 1.5rem;
  }
`;

const SimProfitMargin = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  color: white;
  font-weight: 600;
`;

const SimStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1.5rem;
  background: #fafafa;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SimStatCard = styled.div`
  background: white;
  padding: 1.25rem;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e0e0e0;
  ${props => props.$highlight && `
    border: 2px solid #4CAF50;
    background: #f1f8e9;
  `}
`;

const SimStatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const SimStatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.$positive ? '#4CAF50' : props.$negative ? '#f44336' : '#333'};
`;

const SimStatSubtext = styled.div`
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.25rem;
`;

const SimTierBreakdown = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  
  h3 {
    margin: 0 0 1rem 0;
    color: #333;
  }
`;

const SimTierTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  th {
    background: #f5f5f5;
    font-weight: 600;
    color: #666;
  }
  
  td {
    color: #333;
  }
`;

const SimRecommendations = styled.div`
  padding: 1.5rem;
  background: #fff8e1;
  border-top: 1px solid #ffd54f;
  
  h3 {
    margin: 0 0 1rem 0;
    color: #f57c00;
  }
`;

const SimRecList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  
  li {
    margin: 0.5rem 0;
    color: #333;
  }
`;

export default TreasureHuntAdmin;
